'use client'

import { CollectionContainer } from '@/components/common/collection/collection-container'
import { CollectionMixMatchItem } from '@/components/common/collection/collection-mix-match/collection-mix-match-item'
import {
  ImageZonesOverlay,
  type ImageSize,
  type ImageZone,
} from '@/components/common/image-zones'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'

// Khung ảnh trong CollectionMixMatchItem (aspect-[435/596], object-cover)
const ITEM_FRAME_ASPECT = 435 / 596

export interface MixMatchZonesItem {
  thumbnail: string
  productIds: string[]
  /** Kích thước ảnh gốc, để quy đổi toạ độ zone khi ảnh bị object-cover */
  imageSize?: ImageSize
  /** Vùng sản phẩm, bấm vào mở link sản phẩm */
  zones?: ImageZone[]
}

type Props = {
  title: string
  subTitle?: string
  data: MixMatchZonesItem[]
  /** Định danh section cho ga-tracking-value, VD `seamless-motion` */
  trackingKey?: string
}

/**
 * Mix & match có vùng sản phẩm (zone) trên ảnh.
 * - Bấm vào zone: mở link sản phẩm của zone đó.
 * - Bấm vào phần còn lại của ảnh / nút "Mua ngay": mở modal set như CollectionMixMatch.
 */
export const CollectionMixMatchZones: React.FC<Props> = ({
  title,
  subTitle,
  data,
  trackingKey,
}) => {
  const trackingPrefix = trackingKey ? `mix-match__${trackingKey}` : undefined

  return (
    <CollectionContainer>
      <div className='mb-4'>
        {subTitle ? (
          <p className='font-criteria text-sm uppercase text-neutral-900 lg:text-base'>
            {subTitle}
          </p>
        ) : null}
        <h3 className='font-criteria text-2xl font-medium uppercase text-neutral-900 lg:text-[28px] lg:leading-10'>
          {title}
        </h3>
      </div>

      {/* Mobile: slide ngang, desktop: tắt slide, 4 cột */}
      <Carousel
        opts={{
          align: 'start',
          containScroll: 'trimSnaps',
          breakpoints: { '(min-width: 1024px)': { active: false } },
        }}
        className='w-full'
      >
        <CarouselContent className='-ml-4 lg:-ml-5'>
          {data.map((item, index) => {
            const itemTracking = trackingPrefix
              ? `${trackingPrefix}__set-${index + 1}`
              : undefined

            return (
              <CarouselItem
                key={index}
                className='basis-[80%] pl-4 md:basis-1/2 lg:basis-1/4 lg:pl-5'
              >
                {/* Zone nằm cạnh (không nằm trong) trigger modal để click không mở modal */}
                <div className='relative'>
                  <CollectionMixMatchItem
                    productIds={item.productIds}
                    thumbnail={item.thumbnail}
                    trackingValue={itemTracking}
                  />
                  {/* Zone chỉ áp dụng desktop, mobile giữ như mix & match thường */}
                  {item.zones?.length ? (
                    <ImageZonesOverlay
                      zones={item.zones}
                      imageSize={item.imageSize}
                      frameAspect={ITEM_FRAME_ASPECT}
                      className='max-lg:hidden'
                      trackingValue={itemTracking}
                    />
                  ) : null}
                </div>
              </CarouselItem>
            )
          })}
        </CarouselContent>
        <CarouselPrevious
          className='absolute -left-3 top-1/2 h-7 w-7 -translate-y-1/2 lg:hidden'
          variant='black'
          hideIfStart
          ga-tracking-value={
            trackingPrefix ? `${trackingPrefix}__previous-arrow` : undefined
          }
        />
        <CarouselNext
          className='absolute -right-3 top-1/2 h-7 w-7 -translate-y-1/2 lg:hidden'
          variant='black'
          hideIfEnd
          ga-tracking-value={
            trackingPrefix ? `${trackingPrefix}__next-arrow` : undefined
          }
        />
      </Carousel>
    </CollectionContainer>
  )
}
